import React, { useState } from "react";
import { useApi } from "../../hooks/useApi";
import { useAuth } from "../../context/AuthContext";
import { Card, Spinner, ErrorBox } from "../../components/ui/basics";
import CrudModal from "../../components/normative/CrudModal";
import RequisitiMatrixEditor from "../../components/normative/RequisitiMatrixEditor";

const TARGET_LABEL = { _TOTALE: "Totale (pool aggregato)" };
const PROFILO_LABEL = { _TUTTI: "Tutti i profili" };

export default function MatriceRequisitiSection() {
  const { apiRequest } = useAuth();
  const { data: regioni } = useApi("/normative/regioni");
  const [regione, setRegione] = useState(null);
  const matriceEndpoint = regione
    ? `/normative/requisiti/matrice?regione=${encodeURIComponent(regione)}`
    : null;
  const {
    data: matrice,
    loading,
    error,
    reload,
  } = useApi(matriceEndpoint, [regione]);
  const { data: profiliList } = useApi(
    regione
      ? `/normative/profili?regione=${encodeURIComponent(regione)}`
      : null,
    [regione],
  );
  const { data: areeList } = useApi(
    regione ? `/normative/aree?regione=${encodeURIComponent(regione)}` : null,
    [regione],
  );
  const { data: mansioniList } = useApi("/normative/mansioni");
  const [rapportoModal, setRapportoModal] = useState(null);

  if (!regioni) return <Spinner />;

  const nameOfProfilo = (codice) =>
    PROFILO_LABEL[codice] ||
    profiliList?.find((p) => p.codice === codice)?.name ||
    codice;
  const nameOfTarget = (codice) =>
    TARGET_LABEL[codice] ||
    areeList?.find((a) => a.codice === codice)?.name ||
    mansioniList?.find((m) => m.codice === codice)?.name ||
    codice;
  const isAreaCode = (codice) =>
    (areeList || []).some((a) => a.codice === codice);

  const buildTargetFields = (colKey) => {
    if (matrice.tipologiastandard === "per_mansione")
      return { mansionecodice: colKey };
    if (matrice.tipologiastandard === "per_area") {
      return isAreaCode(colKey)
        ? { areacodice: colKey }
        : { mansionecodice: colKey };
    }
    return {}; // aggregato: nessun target, solo profilo
  };

  const saveCell = async ({ rowKey, colKey, existing, valore }) => {
    if (existing) {
      await apiRequest(`/normative/requisiti/${existing.id}`, {
        method: "PUT",
        body: JSON.stringify({ valore }),
      });
    } else {
      await apiRequest("/normative/requisiti", {
        method: "POST",
        body: JSON.stringify({
          regionecodice: regione,
          profilocodice: rowKey === "_TUTTI" ? null : rowKey,
          tipocalcolo: "MINUTAGGIO",
          unita: matrice.unitamisura || "MIN_DIE",
          valore,
          ...buildTargetFields(colKey),
        }),
      });
    }
    reload();
  };

  const deleteCell = async ({ existing }) => {
    await apiRequest(`/normative/requisiti/${existing.id}`, {
      method: "DELETE",
    });
    reload();
  };

  const rapportoFields = [
    {
      key: "rapportobase",
      label: "Ogni N ospiti",
      type: "number",
      required: true,
    },
    {
      key: "rapportovalore",
      label: "Addetti richiesti",
      type: "number",
      required: true,
    },
    { key: "note", label: "Note" },
  ];

  const submitRapporto = async (values) => {
    const payload = {
      rapportobase: Number(values.rapportobase),
      rapportovalore: Number(values.rapportovalore),
      note: values.note,
    };
    if (rapportoModal.existing) {
      await apiRequest(`/normative/requisiti/${rapportoModal.existing.id}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });
    } else {
      await apiRequest("/normative/requisiti", {
        method: "POST",
        body: JSON.stringify({
          regionecodice: regione,
          profilocodice:
            rapportoModal.rowKey === "_TUTTI" ? null : rapportoModal.rowKey,
          tipocalcolo: "RAPPORTO",
          ...buildTargetFields(rapportoModal.colKey),
          ...payload,
        }),
      });
    }
    reload();
  };

  return (
    <Card
      title="Matrice requisiti"
      subtitle="Righe = profili ospiti, colonne = aree/mansioni. Doppio click su una cella a rapporto per modificarla."
    >
      <select
        value={regione || ""}
        onChange={(e) => setRegione(e.target.value || null)}
        className="border border-gray-300 rounded px-3 py-1.5 text-sm bg-white mb-3"
      >
        <option value="">— Seleziona regione —</option>
        {regioni.map((r) => (
          <option key={r.codice} value={r.codice}>
            {r.name} ({r.tipologiastandard})
          </option>
        ))}
      </select>

      {!regione && (
        <p className="text-sm text-gray-400 py-8 text-center">
          Seleziona una regione.
        </p>
      )}
      {loading && <Spinner />}
      {error && <ErrorBox message={error} onRetry={reload} />}

      {regione && matrice && (
        <>
          <RequisitiMatrixEditor
            rows={matrice.profili.map((p) => ({
              key: p,
              label: nameOfProfilo(p),
            }))}
            columns={matrice.targets.map((t) => ({
              key: t,
              label: nameOfTarget(t),
            }))}
            cells={matrice.cells}
            onSaveCell={saveCell}
            onDeleteCell={deleteCell}
            onEditRapporto={(existing, rowKey, colKey) =>
              setRapportoModal({ existing, rowKey, colKey })
            }
          />
          <button
            onClick={() =>
              setRapportoModal({
                existing: null,
                rowKey: matrice.profili[0],
                colKey: matrice.targets[0],
              })
            }
            className="mt-3 text-xs text-primary-600 hover:underline"
            disabled={!matrice.profili.length || !matrice.targets.length}
          >
            + Nuovo requisito a rapporto
          </button>
        </>
      )}

      {rapportoModal && (
        <CrudModal
          title={
            rapportoModal.existing
              ? "Modifica requisito a rapporto"
              : "Nuovo requisito a rapporto"
          }
          fields={[
            ...(rapportoModal.existing
              ? []
              : [
                  {
                    key: "rowKey",
                    label: "Profilo",
                    type: "select",
                    disabled: true,
                    options: matrice.profili.map((p) => ({
                      value: p,
                      label: nameOfProfilo(p),
                    })),
                  },
                  {
                    key: "colKey",
                    label: "Target",
                    type: "select",
                    disabled: true,
                    options: matrice.targets.map((t) => ({
                      value: t,
                      label: nameOfTarget(t),
                    })),
                  },
                ]),
            ...rapportoFields,
          ]}
          initialValues={{
            rowKey: rapportoModal.rowKey,
            colKey: rapportoModal.colKey,
            rapportobase: rapportoModal.existing?.rapportobase ?? "",
            rapportovalore: rapportoModal.existing?.rapportovalore ?? "",
            note: rapportoModal.existing?.note ?? "",
          }}
          onSubmit={submitRapporto}
          onClose={() => setRapportoModal(null)}
        />
      )}
    </Card>
  );
}
